import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import MobileLogoutConfirmDialog from '../components/mobile/MobileLogoutConfirmDialog'
import { useLayoutMode } from './layout-mode-context'
import { useOfflineNavActions } from './offline-nav-actions-context'
import useLogout from '../hooks/useLogout'
import { listLocalIncidents } from '../offline/incident-store'
import { countUnexportedLocalIncidents } from '../offline/offline-export-filter'
import { canExportLocalCrashData } from '../offline/offline-field-permissions'
import {
  readPersistedAdamsUser,
  resolveSessionInit,
  resolveSessionToken,
} from '../offline/offline-session-utils'

const MobileLogoutContext = createContext(null)

function readSessionUserType() {
  const user = readPersistedAdamsUser()
  return user?.init?.user?.type ?? user?.type
}

export function MobileLogoutProvider({ children }) {
  const { isMobile } = useLayoutMode()
  const navigate = useNavigate()
  const logout = useLogout()
  const offlineNavActions = useOfflineNavActions()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [unexportedCount, setUnexportedCount] = useState(0)

  const canExport = canExportLocalCrashData(readSessionUserType())

  const requestLogout = useCallback(async () => {
    if (!isMobile) {
      await logout()
      return
    }

    const rows = await listLocalIncidents().catch(() => [])
    const count = countUnexportedLocalIncidents(rows)
    if (count > 0) {
      setUnexportedCount(count)
      setConfirmOpen(true)
      return
    }
    await logout()
  }, [isMobile, logout])

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false)
    setUnexportedCount(0)
  }, [])

  const confirmLogout = useCallback(async () => {
    closeConfirm()
    await logout()
  }, [closeConfirm, logout])

  const requestExportInstead = useCallback(() => {
    closeConfirm()
    if (offlineNavActions?.onExport) {
      offlineNavActions.onExport('new_only')
      return
    }
    navigate('/home', {
      state: {
        token: resolveSessionToken(),
        init: resolveSessionInit(),
        openOfflineExport: 'new_only',
      },
    })
  }, [closeConfirm, offlineNavActions, navigate])

  const value = useMemo(
    () => ({ logout, requestLogout }),
    [logout, requestLogout],
  )

  return (
    <MobileLogoutContext.Provider value={value}>
      {children}
      {isMobile ? (
        <MobileLogoutConfirmDialog
          open={confirmOpen}
          count={unexportedCount}
          onCancel={closeConfirm}
          onConfirm={confirmLogout}
          onExport={requestExportInstead}
          showExport={canExport}
        />
      ) : null}
    </MobileLogoutContext.Provider>
  )
}

export function useMobileLogout() {
  const ctx = useContext(MobileLogoutContext)
  const logout = useLogout()

  if (!ctx) {
    return {
      logout,
      requestLogout: logout,
    }
  }

  return ctx
}
